import React, { useState } from 'react';
import { Card, Button, Table, Space, Tag, Input, Select, Typography, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { serviceReceiptService, type ServiceReceiptDTO } from '@/services/serviceReceipt.service';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export const ServiceReceiptsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['service-receipts', statusFilter],
    queryFn: () => serviceReceiptService.list({ status: statusFilter }),
  });

  const handleSubmit = async (id: string) => {
    try {
      await serviceReceiptService.submit(id);
      message.success('Submitted');
      queryClient.invalidateQueries({ queryKey: ['service-receipts'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await serviceReceiptService.approve(id);
      message.success('Approved');
      queryClient.invalidateQueries({ queryKey: ['service-receipts'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed');
    }
  };

  const handlePost = async (id: string) => {
    try {
      await serviceReceiptService.post(id);
      message.success('Posted');
      queryClient.invalidateQueries({ queryKey: ['service-receipts'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed');
    }
  };

  const columns: ColumnsType<ServiceReceiptDTO> = [
    {
      title: 'SR #',
      dataIndex: 'sr_number',
      render: (val, r) => (
        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/service-receipts/${r.id}`)}>
          {val}
        </Button>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'sr_date',
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'PO',
      render: (_, r) => (
        <DocumentRefLink
          to={r.po_id ? `/scm/procurement/purchase-orders/${r.po_id}` : undefined}
          number={r.po_number || r.purchaseOrder?.po_number}
          id={r.po_id}
        />
      ),
    },
    {
      title: 'Milestone',
      render: (_, r) =>
        r.milestone_label ||
        (r.milestone
          ? `#${r.milestone.milestone_number} ${r.milestone.milestone_name}`
          : '—'),
    },
    {
      title: 'Supplier',
      render: (_, r) => r.supplier_name || r.supplier?.supplier_name || '—',
    },
    {
      title: 'Claimed',
      dataIndex: 'claimed_value',
      align: 'right',
      render: (v) => Number(v ?? 0).toFixed(2),
    },
    {
      title: 'Pending billable',
      align: 'right',
      render: (_, r) => {
        const pending = Math.max(0, Number(r.approved_value ?? 0) - Number(r.billed_value ?? 0));
        return pending.toFixed(2);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s, r) => {
        const st = s ?? r.sr_status;
        return <Tag color={statusColors[st] ?? 'default'}>{st}</Tag>;
      },
    },
    {
      title: 'Actions',
      render: (_, r) => {
        const st = r.status ?? r.sr_status;
        const pending = Math.max(0, Number(r.approved_value ?? 0) - Number(r.billed_value ?? 0));
        return (
          <Space size="small">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/service-receipts/${r.id}`)} />
            {st === 'Draft' && (
              <Button type="link" size="small" onClick={() => handleSubmit(r.id)}>
                Submit
              </Button>
            )}
            {st === 'Submitted' && (
              <Button type="link" size="small" onClick={() => handleApprove(r.id)}>
                Approve
              </Button>
            )}
            {st === 'Approved' && (
              <Button type="link" size="small" onClick={() => handlePost(r.id)}>
                Post
              </Button>
            )}
            {st === 'Posted' && pending > 0.01 && (
              <Button
                type="link"
                size="small"
                onClick={() =>
                  navigate(`/scm/procurement/supplier-invoices/new?service_receipt_id=${encodeURIComponent(r.id)}`)
                }
              >
                Create bill
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          Service Receipts
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/service-receipts/new')}>
          New Service Receipt
        </Button>
      </Space>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Draft', 'Submitted', 'Approved', 'Posted', 'Cancelled'].map((s) => ({ value: s, label: s }))}
          />
        </Space>
        <Table rowKey="id" loading={isLoading} columns={columns} dataSource={list} scroll={{ x: 900 }} />
      </Card>
    </div>
  );
};

export default ServiceReceiptsPage;
